import { useState } from 'react';
import { X, Plus } from 'lucide-react';
import { Partner } from '../../types/maintenance';

const OTHER_SUPPLIER_VALUE = '__other__';

interface AddQuotationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (payload: {
    partnerName: string;
    contactEmail?: string;
    contactPhone?: string;
    quotedValue: number;
    deliveryTime?: string;
    notes?: string;
  }) => Promise<void>;
  partners?: Partner[];
  loading?: boolean;
}

export default function AddQuotationModal({
  isOpen,
  onClose,
  onConfirm,
  partners = [],
  loading = false
}: AddQuotationModalProps) {
  const [selectedPartnerId, setSelectedPartnerId] = useState('');
  const [partnerName, setPartnerName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [quotedValue, setQuotedValue] = useState('');
  const [deliveryTime, setDeliveryTime] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');

  const isOtherSupplier = selectedPartnerId === OTHER_SUPPLIER_VALUE;
  const activePartners = partners.filter(p => p.status === 'ativo');

  const resetForm = () => {
    setSelectedPartnerId('');
    setPartnerName('');
    setContactEmail('');
    setContactPhone('');
    setQuotedValue('');
    setDeliveryTime('');
    setNotes('');
    setError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handlePartnerSelect = (value: string) => {
    setSelectedPartnerId(value);
    setError('');

    if (value === OTHER_SUPPLIER_VALUE || value === '') {
      setPartnerName('');
      setContactEmail('');
      setContactPhone('');
      return;
    }

    const partner = activePartners.find(p => p.partnerId === value);
    if (partner) {
      setPartnerName(partner.name);
      setContactEmail(partner.primaryEmail || '');
      setContactPhone(partner.primaryPhone || '');
    }
  };

  if (!isOpen) return null;

  const handleSubmit = async () => {
    setError('');

    if (!selectedPartnerId) {
      setError('Selecione um parceiro ou escolha "Outro fornecedor".');
      return;
    }
    if (isOtherSupplier && !partnerName.trim()) {
      setError('Nome do fornecedor é obrigatório.');
      return;
    }
    if (contactEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)) {
      setError('E-mail de contacto inválido.');
      return;
    }
    const value = parseFloat(quotedValue);
    if (!quotedValue || isNaN(value) || value <= 0) {
      setError('Valor cotado deve ser um número positivo.');
      return;
    }

    try {
      await onConfirm({
        partnerName: partnerName.trim(),
        contactEmail: contactEmail.trim() || undefined,
        contactPhone: contactPhone.trim() || undefined,
        quotedValue: value,
        deliveryTime: deliveryTime.trim() || undefined,
        notes: notes.trim() || undefined,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao adicionar cotação.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-25 transition-opacity" onClick={handleClose} />

        <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Plus className="h-5 w-5 mr-2 text-green-600" />
              Adicionar Cotação
            </h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500"
              disabled={loading}
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="space-y-4">
            {/* Partner Select */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fornecedor <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedPartnerId}
                onChange={(e) => handlePartnerSelect(e.target.value)}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                disabled={loading}
              >
                <option value="">Selecione um fornecedor...</option>
                {activePartners.map((p) => (
                  <option key={p.partnerId} value={p.partnerId}>
                    {p.name}{p.specialization ? ` — ${p.specialization}` : ''}
                  </option>
                ))}
                <option value={OTHER_SUPPLIER_VALUE}>Outro fornecedor (não registado)</option>
              </select>
            </div>

            {/* Partner Name - only editable for "Other" */}
            {isOtherSupplier && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome do Fornecedor <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={partnerName}
                  onChange={(e) => setPartnerName(e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                  placeholder="Ex: Empresa ABC Lda."
                  disabled={loading}
                />
              </div>
            )}

            {/* Contact fields - show when a partner is selected */}
            {selectedPartnerId && (
              <>
                {/* Contact Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    E-mail de Contacto
                  </label>
                  <input
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                    placeholder="parceiro@exemplo.com"
                    disabled={loading}
                    readOnly={!isOtherSupplier}
                  />
                  {!isOtherSupplier && contactEmail && (
                    <p className="mt-1 text-xs text-gray-500">Preenchido automaticamente a partir do registo do parceiro</p>
                  )}
                </div>

                {/* Contact Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefone de Contacto
                  </label>
                  <input
                    type="text"
                    value={contactPhone}
                    onChange={(e) => setContactPhone(e.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                    placeholder="+351 912 345 678"
                    disabled={loading}
                    readOnly={!isOtherSupplier}
                  />
                </div>

                {/* Quoted Value */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valor Cotado (EUR) <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">€</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={quotedValue}
                      onChange={(e) => setQuotedValue(e.target.value.replace(/,/g, '.'))}
                      className="w-full pl-7 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                      placeholder="0.00"
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Delivery Time */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prazo de Entrega
                  </label>
                  <input
                    type="text"
                    value={deliveryTime}
                    onChange={(e) => setDeliveryTime(e.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                    placeholder="Ex: 5 dias úteis"
                    disabled={loading}
                  />
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Observações
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                    placeholder="Se a compra for online, indique o link, a sua preferência e observações que apoiem a avaliação."
                    disabled={loading}
                  />
                </div>
              </>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 mt-4 border-t">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading || !selectedPartnerId}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <span>A adicionar...</span>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Adicionar Cotação
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
